import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import isEqual from 'lodash/isEqual'

import GenericStatusLoading from 'components/GenericStatusLoading'
import PaymentStage from 'containers/stages/PaymentStage'
import IdentificationStage from 'containers/stages/IdentificationStage'
import PersonalInfoStage from 'containers/stages/PersonalInfoStage'
import ModemStage from 'containers/stages/ModemStage'
import SuccessStage from 'containers/stages/SuccessStage'
import AddressCheckStage from 'containers/stages/AddressCheckStage'
import PlanSelectionStage from 'containers/stages/PlanSelectionStage'
import OwnerCheckStage from 'containers/stages/OwnerCheckStage'
import SubmitStage from 'containers/stages/SubmitStage'
import StageIndicator from 'components/StageIndicator'
import OrderSummary from 'components/OrderSummary'
import DialogPlan from 'components/DialogPlan'

import ErrorScreen from 'components/ErrorScreen'
import MultipleAddressStage from 'containers/stages/MultipleAddressStage'
import { trackAbandonments } from 'utilities/tracking'
import {
  STAGES,
  DEFAULT_RESPONSE_ERROR,
  STAGE_CLARIFY_ADDRESS,
  STAGE_OWNER_CHECK,
  STAGE_PERSONAL,
  STAGE_WAITLIST,
  STAGE_CHECK_ADDRESS,
  STAGE_IDENTIFICATION,
  STAGE_PAYMENT,
  STAGE_SUCCESS,
  STAGE_PLAN_SELECT,
  STAGE_SUBMIT,
  STAGE_MODEM_SELECT,
} from './constants'
import { fetchOrderSummary } from './actions'
import styles from './styles.scss'

class CheckoutContainer extends Component {
  constructor(props) {
    super(props)
    this.state = { isDialogPlanOpen: false }
  }

  componentDidMount() {
    this.props.fetchOrderSummary()
    trackAbandonments()
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(this.props.productFeatureAvailability, prevProps.productFeatureAvailability)) {
      const cards = this.getCards()
      const isDialogPlanOpen = (
        this.props.modalSettings.showPlanModal
        && (cards.length > 0)
      )

      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ isDialogPlanOpen })
    }
  }

  onCloseDialogPlan = () => {
    this.setState({ isDialogPlanOpen: false })
  }

  getCards = () => this.props.modalSettings.cards.filter((card) => {
    if (card.availableProductFeatures.length <= 0) {
      return true
    }
    if (this.props.productFeatureAvailability) {
      return card.availableProductFeatures.some(apf => this.props.productFeatureAvailability.includes(apf))
    }
    return false
  })

  render() {
    const { loading, error, currentStage } = this.props
    const {
      // address
      addressSettings,
      multipleAddressSettings,
      // delivery
      modemDeliveryName,
      // identification
      identificationSettings,
      // owner
      ownerSettings,
      // payment
      paymentSettings,
      personalSettings,
      modemDeliverySettings,
      modalSettings,
      planSettings,
      stageSettings,
      // submit
      submitSettings,
      // success
      successSettings,
      // summary,
      summarySettings,
      // general
      genericError,
      phoneNumber,
    } = this.props
    const cards = this.getCards()

    if (error) {
      return (
        <div className={styles.wrapper}>
          <ErrorScreen text={genericError} phoneNumber={phoneNumber} />
        </div>
      )
    }

    let stage
    switch (currentStage) {
      case STAGE_CHECK_ADDRESS:
      case STAGE_WAITLIST:
        stage = <AddressCheckStage isLoading={loading} phoneNumber={phoneNumber} {...addressSettings} />
        break
      case STAGE_CLARIFY_ADDRESS:
        stage = <MultipleAddressStage {...multipleAddressSettings} />
        break
      case STAGE_PLAN_SELECT:
      case STAGE_OWNER_CHECK:
        if (this.state.isDialogPlanOpen) {
          stage = (
            <DialogPlan
              isOpen
              onClose={this.onCloseDialogPlan}
              {...modalSettings}
              cards={cards}
            />
          )
        } else {
          stage = currentStage === STAGE_PLAN_SELECT
            ? <PlanSelectionStage {...planSettings} />
            : <OwnerCheckStage {...ownerSettings} />
        }
        break
      case STAGE_MODEM_SELECT:
        stage = <ModemStage modemSettings={modemDeliverySettings} />
        break
      case STAGE_PERSONAL:
        stage = (
          <PersonalInfoStage
            {...personalSettings}
            modemDeliveryName={modemDeliveryName}
          />
        )
        break
      case STAGE_IDENTIFICATION:
        stage = <IdentificationStage {...identificationSettings} />
        break
      case STAGE_PAYMENT:
        stage = (
          <PaymentStage
            {...paymentSettings}
            stageSettings={stageSettings}
            summarySettings={summarySettings}
          />
        )
        break
      case STAGE_SUBMIT:
        stage = <SubmitStage {...submitSettings} />
        break
      case STAGE_SUCCESS:
        return (
          <div data-stage={STAGE_SUCCESS}>
            <SuccessStage
              {...successSettings}
              stageSettings={stageSettings}
              summarySettings={summarySettings}
            />
          </div>
        )
      case null:
        stage = <GenericStatusLoading />
        break
      default:
        stage = `Invalid stage ${currentStage}`
    }
    return (
      <div className={styles.wrapper} data-stage={currentStage}>
        {currentStage && (
          <OrderSummary
            currentStage={currentStage}
            stageSettings={stageSettings}
            summarySettings={summarySettings}
          />
        )}
        <StageIndicator
          stages={STAGES}
          currentStage={currentStage}
          settings={stageSettings}
        />
        {stage}
      </div>
    )
  }
}

CheckoutContainer.propTypes = {
  // From redux
  loading: PropTypes.bool,
  error: PropTypes.bool,
  currentStage: PropTypes.string,

  // Delivery
  modemDeliveryName: PropTypes.string,

  // From Wagtail
  addressSettings: PropTypes.shape({
    phoneNumber: PropTypes.string,
    notFoundMessage: PropTypes.string,
    notServiceableMessage: PropTypes.string,
    notServiceableYetMessage: PropTypes.string,
    itsComplicatedMessage: PropTypes.string,
    errorMessage: PropTypes.string,
    estimatedDateTitle: PropTypes.string,
    loadingMessage: PropTypes.string,
    queuedMessage: PropTypes.string,
  }),
  multipleAddressSettings: PropTypes.shape({
    title: PropTypes.string,
    subHeading: PropTypes.string,
    helpText: PropTypes.string,
  }),
  modalSettings: PropTypes.shape({
    showPlanModal: PropTypes.bool,
    modalTitle: PropTypes.string,
    modalHelpText: PropTypes.string,
    cards: PropTypes.array,
  }),
  planSettings: PropTypes.shape({
    mainTitle: PropTypes.string,
    tagLine: PropTypes.string,
    plans: PropTypes.array,
    terms: PropTypes.string,
    showPlanModal: PropTypes.bool,
  }),
  modemDeliverySettings: PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.string,
    price: PropTypes.number,
    features: PropTypes.string,
    image: PropTypes.string,
    addressTitle: PropTypes.string,
    attentionToTitle: PropTypes.string,
    disclaimer: PropTypes.string,
    disclaimerLede: PropTypes.string,
  }),
  ownerSettings: PropTypes.shape({
    title: PropTypes.string,
    ownerLabel: PropTypes.string,
    ownerNotLabel: PropTypes.string,
    landlineFieldName: PropTypes.string,
    preText: PropTypes.string,
    phoneNumberText: PropTypes.string,
    notOwnerText: PropTypes.string,
  }),
  personalSettings: PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.string,
    buttonText: PropTypes.string,
  }),
  identificationSettings: PropTypes.shape({
    greenidProduction: PropTypes.bool,
    greenidAccountID: PropTypes.string,
    greenidAPICode: PropTypes.string,
    title: PropTypes.string,
    text: PropTypes.string,
    buttonText: PropTypes.string,
    termsText: PropTypes.string,
  }),
  paymentSettings: PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.string,
    buttonText: PropTypes.string,
    errorMessage: PropTypes.string,
    declinedMessage: PropTypes.string,
  }),
  submitSettings: PropTypes.shape({
    loadingText: PropTypes.string,
  }),
  summarySettings: PropTypes.shape({
    costHeading: PropTypes.string,
    costText: PropTypes.string,
    planInfoText: PropTypes.string,
    modemInfoText: PropTypes.string,
  }),
  successSettings: PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.string,
    appointmentRequiredText: PropTypes.string,
    footerText: PropTypes.string,
    qffText: PropTypes.string,
    qffSuccessText: PropTypes.string,
    nextStepsText: PropTypes.string,
    emailSentText: PropTypes.string,
    helpText: PropTypes.string,
    successConversionTracking: PropTypes.arrayOf(PropTypes.shape({
      activityGroupTagString: PropTypes.string,
      activityTagString: PropTypes.string,
      countingMethod: PropTypes.string,
      floodlightConfigID: PropTypes.string,
    })),
  }),
  stageSettings: PropTypes.shape({
    deliveryShortText: PropTypes.string,
    identificationShortText: PropTypes.string,
    installShortText: PropTypes.string,
    paymentShortText: PropTypes.string,
    personalShortText: PropTypes.string,
    planShortText: PropTypes.string,
  }),
  genericError: PropTypes.string,
  phoneNumber: PropTypes.string,

  // Redux actions
  fetchOrderSummary: PropTypes.func,
  productFeatureAvailability: PropTypes.arrayOf(PropTypes.string),
}

CheckoutContainer.defaultProps = {
  loading: true,
  error: false,
  currentStage: null,
  addressSettings: {},
  modemDeliveryName: '',
  modalSettings: {},
  multipleAddressSettings: {},
  ownerSettings: {},
  paymentSettings: {},
  personalSettings: {},
  planSettings: {},
  stageSettings: {},
  submitSettings: {},
  modemDeliverySettings: {},
  summarySettings: {},
  successSettings: {},
  genericError: DEFAULT_RESPONSE_ERROR,
  phoneNumber: undefined,
  identificationSettings: {},

  fetchOrderSummary: f => f,
  productFeatureAvailability: [],
}

function mapStateToProps(state) {
  // whatever is returned will show up as props
  return {
    loading: state.checkoutReducer.isLoading,
    error: state.checkoutReducer.error,
    currentStage: state.checkoutReducer.currentStage,
    addressResult: state.checkoutReducer.addressResult,
    productFeatureAvailability: state.checkoutReducer.productFeatureAvailability,
  }
}

// Anything returned from this function will end up as props
function mapDispatchToProps(dispatch) {
  // Whenever dispatch is called the result should be passed
  // to all of the reducers
  return bindActionCreators({ fetchOrderSummary }, dispatch)
}

// To promote a component to a container (smart component) - it needs
// to know about this new dispatch method. Make it available as a prop.
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CheckoutContainer)



// WEBPACK FOOTER //
// ./containers/Checkout/index.jsx